import qs from 'qs'
import { LocationDescriptor } from 'history'
import { ReservationType } from '@/models/reservation'
import { ApprovedStatus } from '@/models/reservation-approved-status'

export const getRoomCascadeOptionsByReservations = ({
  reservations,
  reservation,
}: {
  reservations?: ReservationType[]
  reservation?: ReservationType
}) => {
  const reservationsList = reservations?.length ? reservations : reservation ? [reservation] : []
  return reservationsList.flatMap(reserve =>
    reserve?.assignedRoom.map(room => ({
      value: room.guestRoomAssignId,
      label: `${room.roomNumber}(${room.guestRoomAssignShortId})`,
      reservationId: reserve.reservationId,
    })),
  )
}

/**
 * 予約のステータスに応じた詳細画面の遷移先。チェックイン以降はチェックイン ID の画面になる
 * 遷移先を決められない（ステータス不明・ID 不足）場合は null
 */
export const toReservationDetailLocation = ({
  approvedStatus,
  reservationUuid,
  reservationId,
  checkinId,
}: {
  approvedStatus: number | string | null | undefined
  reservationUuid: string | null | undefined
  reservationId: string | null | undefined
  checkinId: string | null | undefined
}): LocationDescriptor | null => {
  if (!reservationId) return null
  const search = qs.stringify({ reservationId })

  switch (`${approvedStatus}`) {
    case ApprovedStatus.Reservation:
      return reservationUuid ? { pathname: `/reservation/${reservationUuid}`, search } : null
    case ApprovedStatus.Checkin:
      return checkinId ? { pathname: `/checkin/${checkinId}`, search } : null
    case ApprovedStatus.Stay:
      return checkinId ? { pathname: `/stay/${checkinId}`, search } : null
    case ApprovedStatus.Checkout:
      return checkinId ? { pathname: `/checkout/${checkinId}`, search } : null
    default:
      return null
  }
}

/**
 * 予約詳細系の画面（予約・チェックイン・滞在中・チェックアウト）を作り直すためのキー
 * 各画面はマウント時にだけ予約を取得するため、同じルートのまま別の予約へ遷移しても表示が切り替わらない。
 * パスと reservationId が変わったときだけ作り直し、画面内の他のクエリ変更では作り直さない
 */
export const reservationDetailRouteKey = (pathname: string, search: string): string => {
  const { reservationId } = qs.parse(search, { ignoreQueryPrefix: true })
  return `${pathname}:${typeof reservationId === 'string' ? reservationId : ''}`
}

/**
 * 身分証アップロードの署名は予約 UUID で発行可否が判定される（チェックアウト済みの予約は拒否される）。
 * 事前チェックイン済みの検索結果には連泊の前半でチェックアウト済みの予約も含まれ得るため、
 * チェックアウト日が今日以降のものを選ぶ。該当が無ければ予約なしとして undefined を返す。
 */
export const pickUploadReservationUuid = (
  reservations: ReadonlyArray<{ id: string; checkoutDate: string }> | undefined,
  today: string,
): string | undefined => reservations?.find(reservation => reservation.checkoutDate >= today)?.id
